export const formatCurrency = (value) =>
    (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

// ─── Seeded Random & Helpers ──────────────────────────────
const MONTH_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function seededRandom(seed) {
    let s = ((seed % 2147483647) + 2147483647) % 2147483647
    if (s === 0) s = 1
    return () => {
        s = (s * 16807) % 2147483647
        return (s - 1) / 2147483646
    }
}

function monthLabel(year, month) {
    return `${MONTH_SHORT[month]}/${String(year).slice(-2)}`
}

function normalizeMonth(year, month) {
    while (month >= 12) { month -= 12; year++ }
    while (month < 0) { month += 12; year-- }
    return { year, month }
}

function getWeeksInMonth(year, month) {
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const weeks = []
    let day = 1
    while (day <= daysInMonth) {
        const endDay = Math.min(day + 6, daysInMonth)
        weeks.push({
            label: `${String(day).padStart(2, '0')}-${String(endDay).padStart(2, '0')} ${MONTH_SHORT[month]}`,
            days: endDay - day + 1,
        })
        day = endDay + 1
    }
    return { weeks, daysInMonth }
}

// ─── Data Generator ────────────────────────────────────────
const VARIATION_LABELS = {
    month: 'vs. mês anterior',
    quarter: 'vs. trimestre anterior',
    semester: 'vs. semestre anterior',
    year: 'vs. ano anterior',
}

export function generateDashboardData(period, referenceDate) {
    const refYear = referenceDate.getFullYear()
    const refMonth = referenceDate.getMonth()

    // Period boundaries
    let startMonth, startYear, numMonths
    switch (period) {
        case 'month':
            startMonth = refMonth; startYear = refYear; numMonths = 1; break
        case 'quarter':
            startMonth = Math.floor(refMonth / 3) * 3; startYear = refYear; numMonths = 3; break
        case 'semester':
            startMonth = Math.floor(refMonth / 6) * 6; startYear = refYear; numMonths = 6; break
        case 'year':
        default:
            startMonth = 0; startYear = refYear; numMonths = 12; break
    }

    const varLabel = VARIATION_LABELS[period]
    const calcVar = (curr, prev) => prev !== 0 ? Number(((curr - prev) / Math.abs(prev) * 100).toFixed(1)) : 0

    // Generate monthly values for this period
    const monthlyData = []
    for (let i = 0; i < numMonths; i++) {
        const { year: y, month: m } = normalizeMonth(startYear, startMonth + i)
        const rng = seededRandom(y * 100 + m + 1)
        monthlyData.push({
            year: y, month: m,
            label: monthLabel(y, m),
            receita: Math.round(110000 + rng() * 50000),
            despesa: Math.round(75000 + rng() * 40000),
        })
    }

    // Previous period for variation
    const prevMonthlyData = []
    for (let i = 0; i < numMonths; i++) {
        const { year: y, month: m } = normalizeMonth(startYear, startMonth + i - numMonths)
        const rng = seededRandom(y * 100 + m + 1)
        prevMonthlyData.push({
            receita: Math.round(110000 + rng() * 50000),
            despesa: Math.round(75000 + rng() * 40000),
        })
    }

    const totalReceita = monthlyData.reduce((s, m) => s + m.receita, 0)
    const totalDespesa = monthlyData.reduce((s, m) => s + m.despesa, 0)
    const totalResultado = totalReceita - totalDespesa
    const prevTotalReceita = prevMonthlyData.reduce((s, m) => s + m.receita, 0)
    const prevTotalDespesa = prevMonthlyData.reduce((s, m) => s + m.despesa, 0)
    const prevTotalResultado = prevTotalReceita - prevTotalDespesa

    const last = monthlyData[monthlyData.length - 1]
    const saldoRng = seededRandom(last.year * 1000 + last.month * 10 + 7)
    const saldoValue = Math.round(250000 + saldoRng() * 80000)
    const prevSaldoValue = Math.round(saldoValue - totalResultado * 0.15)
    const inadRng = seededRandom(last.year * 1000 + last.month * 10 + 3)
    const inadValue = Number((3.5 + inadRng() * 3).toFixed(1))

    // ─── KPI Cards ────────────────────────────
    const kpiResult = [
        { key: 'saldo', title: 'Saldo em Caixa', value: saldoValue, variation: calcVar(saldoValue, prevSaldoValue), variationLabel: varLabel },
        { key: 'receita', title: period === 'month' ? 'Receita do Mês' : 'Receita do Período', value: totalReceita, variation: calcVar(totalReceita, prevTotalReceita), variationLabel: varLabel },
        { key: 'despesa', title: period === 'month' ? 'Despesa do Mês' : 'Despesa do Período', value: totalDespesa, variation: calcVar(totalDespesa, prevTotalDespesa), variationLabel: varLabel },
        { key: 'resultado', title: 'Resultado Líquido', value: totalResultado, variation: calcVar(totalResultado, prevTotalResultado), variationLabel: varLabel },
        { key: 'inadimplencia', title: 'Inadimplência', value: inadValue, variation: Number((-0.5 - inadRng() * 2).toFixed(1)), variationLabel: varLabel, isPercentage: true },
    ]

    // ─── 1. Cash Flow Area ────────────────────
    const cfPoints = []
    let cashFlowTodayIdx

    if (period === 'month') {
        // Weekly granularity for month mode
        const { weeks, daysInMonth } = getWeeksInMonth(startYear, startMonth)
        const wkRng = seededRandom(startYear * 1000 + startMonth * 10 + 200)
        const monthReceita = monthlyData[0].receita
        const monthDespesa = monthlyData[0].despesa
        let wkRunning = saldoValue - totalResultado

        // Determine which week "today" falls in
        const today = referenceDate.getDate()
        let todayWeekIdx = 0
        let dayAcc = 0
        for (let i = 0; i < weeks.length; i++) {
            dayAcc += weeks[i].days
            if (today <= dayAcc) { todayWeekIdx = i; break }
        }

        weeks.forEach((week, i) => {
            const fraction = week.days / daysInMonth
            const wkR = Math.round(monthReceita * fraction * (0.8 + wkRng() * 0.4))
            const wkD = Math.round(monthDespesa * fraction * (0.8 + wkRng() * 0.4))
            wkRunning += wkR - wkD

            if (i <= todayWeekIdx) {
                // Past and current week: realized (bridge point also gets projected)
                cfPoints.push({ label: week.label, realized: Math.round(wkRunning), projected: i === todayWeekIdx ? Math.round(wkRunning) : null })
            } else {
                // Future weeks: projected only
                cfPoints.push({ label: week.label, realized: null, projected: Math.round(wkRunning) })
            }
        })

        cashFlowTodayIdx = todayWeekIdx

        // 1 projected week into next month
        const { month: nm } = normalizeMonth(startYear, startMonth + 1)
        cfPoints.push({
            label: `01-07 ${MONTH_SHORT[nm]}`,
            realized: null,
            projected: Math.round(wkRunning + 8000 + wkRng() * 15000),
        })
    } else if (period === 'quarter') {
        // Weekly granularity for quarter mode — rolling window centered on today
        const qRng = seededRandom(startYear * 1000 + startMonth * 10 + 300)

        // Build window: ~1.5 months back to ~1.5 months forward from referenceDate
        const windowStart = new Date(refYear, refMonth, referenceDate.getDate())
        windowStart.setDate(windowStart.getDate() - 45)
        // Align to start of that week (Monday-aligned, day 1 of first 7-day block)
        const wsDay = windowStart.getDate()
        windowStart.setDate(wsDay - ((wsDay - 1) % 7))

        const windowEnd = new Date(refYear, refMonth, referenceDate.getDate())
        windowEnd.setDate(windowEnd.getDate() + 45)

        // Generate 7-day week buckets across the window
        const allWeeks = []
        const cursor = new Date(windowStart)
        while (cursor < windowEnd) {
            const wkStart = new Date(cursor)
            const wkEnd = new Date(cursor)
            wkEnd.setDate(wkEnd.getDate() + 6)

            const sm = wkStart.getMonth()
            const em = wkEnd.getMonth()
            let label
            if (sm === em) {
                label = `${String(wkStart.getDate()).padStart(2, '0')}-${String(wkEnd.getDate()).padStart(2, '0')} ${MONTH_SHORT[sm]}`
            } else {
                label = `${String(wkStart.getDate()).padStart(2, '0')} ${MONTH_SHORT[sm]}-${String(wkEnd.getDate()).padStart(2, '0')} ${MONTH_SHORT[em]}`
            }

            allWeeks.push({ label, startDate: new Date(wkStart), endDate: new Date(wkEnd) })
            cursor.setDate(cursor.getDate() + 7)
        }

        // Determine which week "today" falls in
        let todayWeekIdx = 0
        for (let i = 0; i < allWeeks.length; i++) {
            if (referenceDate >= allWeeks[i].startDate && referenceDate <= allWeeks[i].endDate) {
                todayWeekIdx = i
                break
            }
        }

        // Generate weekly running cash values
        let wkRunning = saldoValue - totalResultado
        allWeeks.forEach((week, i) => {
            // Map week to a monthly data bucket for proportional calculation
            const wkMid = new Date(week.startDate)
            wkMid.setDate(wkMid.getDate() + 3)
            const wkMonth = wkMid.getMonth()
            const wkYear = wkMid.getFullYear()
            // Find matching monthlyData entry or use closest
            const mdIdx = monthlyData.findIndex(md => md.year === wkYear && md.month === wkMonth)
            const md = mdIdx >= 0 ? monthlyData[mdIdx] : monthlyData[monthlyData.length - 1]
            const daysInThatMonth = new Date(wkYear, wkMonth + 1, 0).getDate()
            const fraction = 7 / daysInThatMonth

            const wkR = Math.round(md.receita * fraction * (0.85 + qRng() * 0.3))
            const wkD = Math.round(md.despesa * fraction * (0.85 + qRng() * 0.3))
            wkRunning += wkR - wkD

            if (i <= todayWeekIdx) {
                cfPoints.push({ label: week.label, realized: Math.round(wkRunning), projected: i === todayWeekIdx ? Math.round(wkRunning) : null })
            } else {
                cfPoints.push({ label: week.label, realized: null, projected: Math.round(wkRunning) })
            }
        })

        cashFlowTodayIdx = todayWeekIdx
    } else {
        // Monthly granularity for semester/year
        const todayMonthOffset = (refYear - startYear) * 12 + (refMonth - startMonth)
        const clampedToday = Math.max(0, Math.min(todayMonthOffset, numMonths - 1))

        let runningCash = saldoValue - totalResultado
        for (let i = 0; i < numMonths; i++) {
            const md = monthlyData[i]
            runningCash += md.receita - md.despesa

            if (i <= clampedToday) {
                cfPoints.push({ label: md.label, realized: Math.round(runningCash), projected: i === clampedToday ? Math.round(runningCash) : null })
            } else {
                cfPoints.push({ label: md.label, realized: null, projected: Math.round(runningCash) })
            }
        }
        cashFlowTodayIdx = clampedToday

        // 3 projected months beyond period
        for (let i = 0; i < 3; i++) {
            const { year: y, month: m } = normalizeMonth(startYear, startMonth + numMonths + i)
            const rng = seededRandom(y * 100 + m + 1)
            runningCash += Math.round(20000 + rng() * 30000)
            cfPoints.push({ label: monthLabel(y, m), realized: null, projected: Math.round(runningCash) })
        }
    }

    const cashFlowResult = {
        categories: cfPoints.map(p => p.label),
        realizado: cfPoints.map(p => p.realized),
        projetado: cfPoints.map(p => p.projected),
        todayIndex: cashFlowTodayIdx,
    }

    // ─── 2. Revenue vs Expense Bars ───────────
    let revExpItems
    if (period === 'month') {
        // Weekly bars for month mode
        const { weeks, daysInMonth } = getWeeksInMonth(startYear, startMonth)
        const revExpRng = seededRandom(startYear * 1000 + startMonth * 10 + 201)
        const monthR = monthlyData[0].receita
        const monthD = monthlyData[0].despesa

        revExpItems = weeks.map(week => {
            const fraction = week.days / daysInMonth
            return {
                label: week.label,
                receita: Math.round(monthR * fraction * (0.8 + revExpRng() * 0.4)),
                despesa: Math.round(monthD * fraction * (0.8 + revExpRng() * 0.4)),
            }
        })
    } else {
        revExpItems = monthlyData
    }

    const revenueExpenseResult = {
        categories: revExpItems.map(m => m.label),
        receitas: revExpItems.map(m => m.receita),
        despesas: revExpItems.map(m => m.despesa),
    }

    // ─── 3 & 4. Donut Charts ─────────────────
    const donutRng = seededRandom(startYear * 1000 + startMonth * 10 + 5)
    const baseRevProps = [0.45, 0.25, 0.15, 0.08, 0.07]
    const revProps = baseRevProps.map(p => p + (donutRng() - 0.5) * 0.04)
    const revPropSum = revProps.reduce((a, b) => a + b, 0)

    const revenueByCategoryResult = {
        labels: ['Vendas de Produtos', 'Prestação de Serviços', 'Recorrência/Assinaturas', 'Comissões', 'Outros'],
        series: revProps.map(p => Math.round(totalReceita * p / revPropSum)),
        colors: ['#3B82F6', '#8B5CF6', '#06B6D4', '#F59E0B', '#94A3B8'],
    }

    const baseExpProps = [0.40, 0.18, 0.15, 0.12, 0.10, 0.05]
    const expProps = baseExpProps.map(p => p + (donutRng() - 0.5) * 0.03)
    const expPropSum = expProps.reduce((a, b) => a + b, 0)

    const expenseByCategoryResult = {
        labels: ['Folha de Pagamento', 'Impostos', 'Infraestrutura/TI', 'Marketing', 'Fornecedores', 'Administrativo'],
        series: expProps.map(p => Math.round(totalDespesa * p / expPropSum)),
        colors: ['#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#94A3B8'],
    }

    // ─── 5. Aging — static snapshot ──────────
    const agRng = seededRandom(last.year * 100 + last.month + 42)
    const agingResult = {
        categories: ['A vencer (próx. 30 dias)', 'Vencido 1-15 dias', 'Vencido 16-30 dias', 'Vencido 31-60 dias', 'Vencido 60+ dias'],
        values: [
            Math.round(70000 + agRng() * 30000),
            Math.round(25000 + agRng() * 15000),
            Math.round(12000 + agRng() * 12000),
            Math.round(5000 + agRng() * 8000),
            Math.round(3000 + agRng() * 6000),
        ],
        colors: ['#22C55E', '#84CC16', '#F59E0B', '#F97316', '#EF4444'],
    }

    // ─── 6. Waterfall DRE — scaled by period ─
    const wfRng = seededRandom(startYear * 100 + startMonth + 77)
    const recBruta = Math.round((130000 + wfRng() * 40000) * numMonths)
    const deducoes = Math.round(recBruta * (0.07 + wfRng() * 0.04))
    const recLiquida = recBruta - deducoes
    const custosVar = Math.round(recLiquida * (0.30 + wfRng() * 0.10))
    const margemContrib = recLiquida - custosVar
    const custosFixos = Math.round((40000 + wfRng() * 20000) * numMonths)
    const resultOp = margemContrib - custosFixos
    const impostos = resultOp > 0 ? Math.round(resultOp * (0.15 + wfRng() * 0.10)) : 0
    const lucroLiq = resultOp > 0 ? resultOp - impostos : resultOp

    const waterfallResult = [
        { label: 'Receita Bruta', value: recBruta, isTotal: false, isPositive: true },
        { label: 'Deduções', value: -deducoes, isTotal: false, isPositive: false },
        { label: 'Receita Líquida', value: recLiquida, isTotal: true, isPositive: true },
        { label: 'Custos Variáveis', value: -custosVar, isTotal: false, isPositive: false },
        { label: 'Margem de Contribuição', value: margemContrib, isTotal: true, isPositive: true },
        { label: 'Custos Fixos', value: -custosFixos, isTotal: false, isPositive: false },
        { label: 'Resultado Operacional', value: resultOp, isTotal: true, isPositive: resultOp >= 0 },
        { label: 'Impostos', value: -impostos, isTotal: false, isPositive: false },
        { label: 'Lucro Líquido', value: lucroLiq, isTotal: true, isPositive: lucroLiq >= 0 },
    ]

    // ─── 7. Sparklines — static (last 6 months)
    const spRng = seededRandom(last.year * 100 + last.month + 13)
    const genSpark = (base, range) => Array.from({ length: 6 }, () => Number((base + spRng() * range).toFixed(1)))

    const sparklineResult = [
        { label: 'Margem Bruta', value: `${(55 + spRng() * 10).toFixed(1)}%`, variation: Number((spRng() * 4 - 1).toFixed(1)), data: genSpark(52, 10), suffix: '%', variationLabel: varLabel },
        { label: 'Margem Líquida', value: `${(10 + spRng() * 5).toFixed(1)}%`, variation: Number((spRng() * 3 - 0.5).toFixed(1)), data: genSpark(8, 6), suffix: '%', variationLabel: varLabel },
        { label: 'Ticket Médio', value: `R$ ${Math.round(2000 + spRng() * 800)}`, variation: Number((spRng() * 8 - 2).toFixed(1)), data: genSpark(1800, 900), prefix: 'R$', variationLabel: varLabel },
        { label: 'Inadimplência', value: `${(3.5 + spRng() * 3).toFixed(1)}%`, variation: Number((-spRng() * 2).toFixed(1)), data: genSpark(4, 4), suffix: '%', invertColor: true, variationLabel: varLabel },
    ]

    // ─── 8. Budget vs Actual — scaled ────────
    const bvaRng = seededRandom(startYear * 100 + startMonth + 55)
    const bvaBase = [42000, 10000, 15000, 18000, 10500, 5000]
    const budgetResult = {
        categories: ['Folha de Pagamento', 'Marketing', 'Infraestrutura/TI', 'Impostos', 'Fornecedores', 'Administrativo'],
        orcado: bvaBase.map(v => Math.round(v * numMonths * (0.95 + bvaRng() * 0.10))),
        realizado: bvaBase.map(v => Math.round(v * numMonths * (0.85 + bvaRng() * 0.25))),
    }

    // ─── 9. Top Clients — scaled ─────────────
    const tcRng = seededRandom(startYear * 100 + startMonth + 33)
    const clientNames = [
        'Construtora Horizonte Ltda', 'TechSol Sistemas S.A.', 'Distribuidora São Paulo',
        'Grupo Atlântico Comercial', 'Metalúrgica Progresso', 'Agro Brasil Exportação',
        'Logística Express Ltda', 'Indústria Carioca S.A.', 'Consultoria Vértice', 'Rede Alimentar Mineira',
    ]
    const topClientsResult = clientNames.map((name, i) => ({
        name,
        value: Math.round((35000 - i * 3000 + tcRng() * 5000) * numMonths),
    })).sort((a, b) => b.value - a.value)

    // ─── 10. Accounts Payable — static ───────
    const apRng = seededRandom(last.year * 100 + last.month + 99)
    const accountsPayableResult = {
        categories: ['Semana 1', 'Semana 2', 'Semana 3', 'Semana 4'],
        values: [
            Math.round(20000 + apRng() * 25000),
            Math.round(20000 + apRng() * 25000),
            Math.round(15000 + apRng() * 20000),
            Math.round(20000 + apRng() * 25000),
        ],
        threshold: 35000,
    }

    return {
        kpiData: kpiResult,
        cashFlowData: cashFlowResult,
        revenueExpenseData: revenueExpenseResult,
        revenueByCategoryData: revenueByCategoryResult,
        expenseByCategoryData: expenseByCategoryResult,
        agingData: agingResult,
        waterfallData: waterfallResult,
        sparklineData: sparklineResult,
        budgetVsActualData: budgetResult,
        topClientsData: topClientsResult,
        accountsPayableData: accountsPayableResult,
    }
}

// ─── KPI Cards ───────────────────────────────────────────
export const kpiData = [
    { key: 'saldo', title: 'Saldo em Caixa', value: 284750.00, variation: 5.2, variationLabel: 'vs. mês anterior' },
    { key: 'receita', title: 'Receita do Mês', value: 137420.00, variation: 8.1, variationLabel: 'vs. mês anterior' },
    { key: 'despesa', title: 'Despesa do Mês', value: 98630.00, variation: -3.4, variationLabel: 'vs. mês anterior' },
    { key: 'resultado', title: 'Resultado Líquido', value: 38790.00, variation: 22.6, variationLabel: 'vs. mês anterior' },
    { key: 'inadimplencia', title: 'Inadimplência', value: 4.7, variation: -1.2, variationLabel: 'vs. mês anterior', isPercentage: true },
]

// ─── 1. Fluxo de Caixa — Realizado vs. Projetado ────────
const months9 = ['Out/25', 'Nov/25', 'Dez/25', 'Jan/26', 'Fev/26', 'Mar/26', 'Abr/26', 'Mai/26', 'Jun/26']
export const cashFlowData = {
    categories: months9,
    realizado: [210000, 225000, 218000, 245000, 260000, 284750, null, null, null],
    projetado: [null, null, null, null, null, 284750, 298000, 315000, 332000],
    todayIndex: 5, // Mar/26
}

// ─── 2. Receitas vs. Despesas (Mensal) ───────────────────
const months12 = ['Abr/25', 'Mai/25', 'Jun/25', 'Jul/25', 'Ago/25', 'Set/25', 'Out/25', 'Nov/25', 'Dez/25', 'Jan/26', 'Fev/26', 'Mar/26']
export const revenueExpenseData = {
    categories: months12,
    receitas: [98500, 105200, 112800, 95600, 118400, 125700, 131200, 110800, 142500, 128900, 133600, 137420],
    despesas: [78200, 82400, 88100, 79800, 91500, 96300, 102400, 89700, 108200, 95400, 101800, 98630],
}

// ─── 3. Composição de Receitas ───────────────────────────
export const revenueByCategoryData = {
    labels: ['Vendas de Produtos', 'Prestação de Serviços', 'Recorrência/Assinaturas', 'Comissões', 'Outros'],
    series: [61839, 34355, 27484, 8245, 5497],
    colors: ['#3B82F6', '#8B5CF6', '#06B6D4', '#F59E0B', '#94A3B8'],
}

// ─── 4. Composição de Despesas ───────────────────────────
export const expenseByCategoryData = {
    labels: ['Folha de Pagamento', 'Impostos', 'Infraestrutura/TI', 'Marketing', 'Fornecedores', 'Administrativo'],
    series: [39452, 17754, 14795, 11836, 9863, 4930],
    colors: ['#EF4444', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#94A3B8'],
}

// ─── 5. Aging de Contas a Receber ────────────────────────
export const agingData = {
    categories: ['A vencer (próx. 30 dias)', 'Vencido 1-15 dias', 'Vencido 16-30 dias', 'Vencido 31-60 dias', 'Vencido 60+ dias'],
    values: [85400, 32100, 18700, 9200, 5800],
    colors: ['#22C55E', '#84CC16', '#F59E0B', '#F97316', '#EF4444'],
}

// ─── 6. DRE Visual (Waterfall) ───────────────────────────
export const waterfallData = [
    { label: 'Receita Bruta', value: 150000, isTotal: false, isPositive: true },
    { label: 'Deduções', value: -12500, isTotal: false, isPositive: false },
    { label: 'Receita Líquida', value: 137500, isTotal: true, isPositive: true },
    { label: 'Custos Variáveis', value: -48200, isTotal: false, isPositive: false },
    { label: 'Margem de Contribuição', value: 89300, isTotal: true, isPositive: true },
    { label: 'Custos Fixos', value: -52100, isTotal: false, isPositive: false },
    { label: 'Resultado Operacional', value: 37200, isTotal: true, isPositive: true },
    { label: 'Impostos', value: -19200, isTotal: false, isPositive: false },
    { label: 'Lucro Líquido', value: 18000, isTotal: true, isPositive: true },
]

// ─── 7. Sparkline Indicadores ────────────────────────────
export const sparklineData = [
    { label: 'Margem Bruta', value: '59.5%', variation: 2.1, data: [54.2, 55.8, 57.1, 56.3, 58.9, 59.5], suffix: '%' },
    { label: 'Margem Líquida', value: '12.0%', variation: 1.8, data: [9.5, 10.2, 10.8, 11.1, 11.5, 12.0], suffix: '%' },
    { label: 'Ticket Médio', value: 'R$ 2.480', variation: 5.3, data: [2100, 2200, 2150, 2350, 2420, 2480], prefix: 'R$' },
    { label: 'Inadimplência', value: '4.7%', variation: -1.2, data: [6.2, 5.8, 5.5, 5.1, 4.9, 4.7], suffix: '%', invertColor: true },
]

// ─── 8. Orçado vs. Realizado ─────────────────────────────
export const budgetVsActualData = {
    categories: ['Folha de Pagamento', 'Marketing', 'Infraestrutura/TI', 'Impostos', 'Fornecedores', 'Administrativo'],
    orcado:    [42000, 10000, 15000, 18000, 10500, 5000],
    realizado: [39452, 11836, 14795, 17754, 9863, 4930],
}

// ─── 9. Top 10 Clientes por Receita ─────────────────────
export const topClientsData = [
    { name: 'Construtora Horizonte Ltda', value: 34200 },
    { name: 'TechSol Sistemas S.A.', value: 28750 },
    { name: 'Distribuidora São Paulo', value: 22100 },
    { name: 'Grupo Atlântico Comercial', value: 18900 },
    { name: 'Metalúrgica Progresso', value: 15400 },
    { name: 'Agro Brasil Exportação', value: 12800 },
    { name: 'Logística Express Ltda', value: 10500 },
    { name: 'Indústria Carioca S.A.', value: 8200 },
    { name: 'Consultoria Vértice', value: 6300 },
    { name: 'Rede Alimentar Mineira', value: 5100 },
]

// ─── 10. Contas a Pagar — Próximos 30 Dias ──────────────
export const accountsPayableData = {
    categories: ['Semana 1 (10-16 Mar)', 'Semana 2 (17-23 Mar)', 'Semana 3 (24-30 Mar)', 'Semana 4 (31 Mar-06 Abr)'],
    values: [28500, 42300, 18700, 35100],
    threshold: 35000, // valor considerado "alto"
}
